import { useEffect, useRef, useState } from "react";
import { ReactComponent as CloseIcon } from "../../../assets/icons/close.svg";
import PrimaryButton from "../../DesignSystem/Buttons/PrimaryButton";
import SecondaryButton from "../../DesignSystem/Buttons/SecondaryButton";
import UploadedFileList from "../../DesignSystem/Upload/UploadedFileList";
import AlertBanner from "../../DesignSystem/AlertBanner/AlertBanner";
import { logReferenceAudit } from "../../../backend/Recruitment/Referencing/referenceAuditLog";
import { canSendReferenceEmailForStatus } from "./constants/recruitment";
import "../../../css/Recruitment/ReferenceEmailPreviewModal.css";

const formatDate = (value) => {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : new Intl.DateTimeFormat("en-GB").format(date);
};

const toTextLines = (value) =>
  String(value || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

const TEMPLATE_QUESTION_REPLACEMENTS = Object.freeze({
  "What is your relationship to the adviser?": "What is your relationship to Adviser?",
  "How long have you known the adviser?": "How long you have known Adviser?",
  "Can you please confirm, to the best of your knowledge, what the adviser was doing between the relevant unemployment or self-employment dates?":
    "Can you please confirm, to the best of your knowledge, what Adviser was doing between (Unemployment dates/self-employed dates)?",
  "In your experience with the adviser, have you found the adviser to be reliable, patient and compassionate?":
    "In your experience with Adviser, have you found Adviser to be, reliable, patient and compassionate?",
  "How would you best describe the adviser?": "How would you best describe Adviser?",
  "How long have you been the accountant for the adviser?":
    "How long have you been an accountant for?",
  "How long have you been working with the adviser?":
    "How long have you been working with Adviser for?",
  "Can you confirm what the adviser was doing during this period?":
    "Can you confirm what Adviser was doing between this period (Dates)?",
  "During which period was the adviser enrolled at the educational institution?":
    "During which period(s) was Adviser Name enrolled at Educational Institute?",
  "In your opinion, did the adviser demonstrate honesty and integrity during their studies?":
    "In your opinion, did Adviser Name demonstrate honesty and integrity during his studies?",
});

const personaliseQuestion = (question, applicantName, institutionName = "") =>
  (TEMPLATE_QUESTION_REPLACEMENTS[question] || question)
    .replace(/\bAdviser Name\b/gi, applicantName || "the applicant")
    .replace(/\bthe adviser\b/gi, applicantName || "the applicant")
    .replace(/\badviser\b/gi, applicantName || "the applicant")
    .replace(
      /\bEducational Institute\b/gi,
      institutionName || "the educational institution",
    );

const isCurrentlyEmployed = (value) =>
  value === true || ["yes", "true"].includes(String(value || "").toLowerCase());

const getUkGreeting = () => {
  const ukHour = Number(
    new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/London",
      hour: "2-digit",
      hourCycle: "h23",
    }).format(new Date()),
  );

  if (ukHour < 12) return "Good morning";
  if (ukHour < 17) return "Good afternoon";
  return "Good evening";
};

const ReferenceEmailPreviewModal = ({
  open,
  onClose,
  onSend,
  form,
  applicantName,
  nationalInsuranceNumber,
  history,
  applicationId,
  referenceId,
  referenceKind,
  canSend = true,
  sendDisabledReason = "",
  needsSave = false,
  isSaving = false,
  onSave,
  onSaveConfirmationChange,
}) => {
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [saveError, setSaveError] = useState("");
  const loggedPreviewKeyRef = useRef("");

  useEffect(() => {
    if (!open) return undefined;
    setIsSending(false);
    setSendError("");
    setSaveError("");
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      loggedPreviewKeyRef.current = "";
      return;
    }
    if (!applicationId || !referenceId) return;
    const previewKey = `${applicationId}:${referenceId}`;
    if (loggedPreviewKeyRef.current === previewKey) return;
    loggedPreviewKeyRef.current = previewKey;
    void logReferenceAudit({
      applicationId,
      referenceId,
      referenceKind: referenceKind || form.referenceKind,
      action: "email_preview_opened",
      changes: [
        {
          fieldPath: "emailPreview.open",
          previousValue: false,
          newValue: true,
        },
      ],
      details: { email: form },
    }).catch((auditError) => console.error("Reference audit failed", auditError));
  }, [open, applicationId, referenceId, referenceKind, form]);

  if (!open) return null;

  const role =
    form.jobTitle ||
    history?.currentJobTitle ||
    history?.jobTitle ||
    history?.position ||
    history?.role ||
    "";
  const additionalQuestions = toTextLines(form.additionalQuestion);
  const messageAdditionLines = toTextLines(form.messageAdditions);
  const requiresAttachments =
    referenceKind === "work-history" &&
    ["regulated", "non-regulated"].includes(form.referenceType);
  const isEducationalReference =
    form.referenceKind === "educational" ||
    (referenceKind === "work-history" && form.referenceType === "educational");
  const hasConsentAttachment = form.attachments?.some(
    (attachment) => attachment?.source === "reference-consent-form",
  );
  const hasAdviserPermission =
    form.adviserPermission === true ||
    String(form.adviserPermission || "").trim().toLowerCase() === "yes";
  const statusAllowsEmail = canSendReferenceEmailForStatus(form.status);
  const hasReferenceRequestedDate = Boolean(form.referenceSent);
  const isFollowUpEmail =
    Number(form.referenceFollowUpCount || 0) > 0 ||
    String(form.status || "").startsWith("follow-up-");
  const previousContactDate = form.lastFollowedUpOn || form.referenceSent;
  const followUpIntroduction =
    isFollowUpEmail && previousContactDate ? (
      <p>
        I am following up on my previous reference request sent on{" "}
        {formatDate(previousContactDate)}. Please see the original request
        details below.
      </p>
    ) : null;
  const warningMessages = [
    requiresAttachments
      ? "Note: Please review the extracted information before proceeding to send the email. Please ensure that the appropriate reference template and Reference Authorisation Form are attached, and change the template date to the date of requesting the reference."
      : "Note: Please review the reference information before proceeding to send the email.",
    ...(!statusAllowsEmail
      ? ["The current reference status does not allow emails to be sent."]
      : []),
  ];
  const infoMessages = [
    ...(needsSave
      ? ["This reference has unsaved changes. Confirm the details and save below before sending the email."]
      : !canSend
      ? [sendDisabledReason || "Save this reference before sending the email."]
      : []),
    ...(hasReferenceRequestedDate
      ? ["This reference request has already been sent. The original request date is shown in the reference details."]
      : []),
  ];
  const errorMessages = [
    ...(requiresAttachments && !hasConsentAttachment
      ? ["The Reference Authorisation Form is required before this email can be sent."]
      : []),
    ...(!hasAdviserPermission
      ? ["Send Email is unavailable because the adviser’s permission to contact for a reference is not set to Yes. Close this preview, select Yes, save the reference, and reopen the request."]
      : []),
    ...(sendError ? [sendError] : []),
    ...(saveError ? [saveError] : []),
  ];
  const renderAlertMessages = (messages) => (
    <div className="fit-column gap-s">
      {messages.map((message) => <div key={message}>{message}</div>)}
    </div>
  );

  const handleSend = async () => {
    setIsSending(true);
    setSendError("");
    try {
      await logReferenceAudit({
        applicationId,
        referenceId,
        referenceKind: referenceKind || form.referenceKind,
        action: "email_send_requested",
        changes: [{ fieldPath: "emailPreview.sendRequested", previousValue: false, newValue: true }],
        details: { hasConsentAttachment, email: form },
      });
      await onSend?.();
      await logReferenceAudit({
        applicationId,
        referenceId,
        referenceKind: referenceKind || form.referenceKind,
        action: "email_send_completed",
        changes: [{ fieldPath: "emailPreview.sent", previousValue: false, newValue: true }],
        details: { email: form },
      });
    } catch (error) {
      setSendError(error?.message || "The reference email could not be sent.");
    } finally {
      setIsSending(false);
    }
  };

  const handleSave = async () => {
    setSaveError("");
    if (!form.saveConfirmed) {
      setSaveError("Confirm that all reference details are correct before saving.");
      return;
    }
    const saved = await onSave?.();
    if (saved === false) {
      setSaveError("The reference could not be saved. Please review the highlighted fields.");
    }
  };

  return (
    <div
      className="reference-email-modal-overlay"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="gap-m p-m bg-white card-corner-radius reference-email-modal"
        role="dialog"
        aria-modal="true"
      >
        <div className="reference-email-modal-header row-space-between">
          <div id="FP_Subtitle_SemiBold" className="Content-SemiBold primary">
            Reference Email
          </div>
          <CloseIcon onClick={onClose} />
        </div>
        <div className="reference-email-modal-content fit-column gap-m">
        <AlertBanner
          type="warning"
          message={renderAlertMessages(warningMessages)}
          showClose={false}
        />
        {errorMessages.length > 0 && (
          <AlertBanner
            type="error"
            message={renderAlertMessages(errorMessages)}
            showClose={false}
          />
        )}
        {infoMessages.length > 0 && (
          <AlertBanner
            type="info"
            message={renderAlertMessages(infoMessages)}
            showClose={false}
          />
        )}

        {[
          ["From", form.sender],
          ["To", form.recipients],
          ["CC", form.cc],
          ["BCC", form.bcc],
          ["Subject", form.subject],
        ].map(([label, value]) => (
          <div
            className="reference-email-recipient-row row align-center gap-s"
            key={label}
          >
            <span className="FP_Content_Semibold text-black">{label}:</span>
            <div className="reference-email-preview-value flex-1 bg-ghost-white p-xs all-radius-s FP_Content">
              {value || ""}
            </div>
          </div>
        ))}
        <div className="reference-email-recipient-row row align-center gap-s">
          <span className="FP_Content_Semibold text-black">Attachments:</span>
          <div className="flex-1">
            {form.attachments.length ? (
              <UploadedFileList files={form.attachments} removable={false} />
            ) : (
              <div className="reference-email-preview-value flex-1 bg-ghost-white p-xs all-radius-s FP_Content">
                {/* {value || ""} */}No attachments added
                {/* <span className="text-grey FP_Help_Text text-align-left"></span> */}
              </div>
            )}
          </div>
        </div>
          <div className="FP_Content_Semibold text-black">Message Body:</div>
          <div className="reference-email-preview-message bg-ghost-white p-s card-corner-radius FP_Content">
          {Object.prototype.hasOwnProperty.call(form, "messageBody") ? (
            <>
              <p>
                {getUkGreeting()}
                ,
              </p>
              {followUpIntroduction}
              <div className="reference-email-preview-body-copy">
                {form.messageBody}
              </div>
              <p>
                Thanks &amp; Regards,
                <br />
                Rosemount Financial Solutions (IFA) Ltd
              </p>
            </>
          ) : form.referenceKind === "personal" ? (
            <>
            <p>
              {getUkGreeting()},
            </p>
            {followUpIntroduction}
            <p>I hope this email finds you well.</p>
            <p>
              This is a personal reference for one our recruits, {applicantName || "the applicant"}.
            </p>
            {(form.questions?.length > 0 || additionalQuestions.length > 0) && (
              <>
                <p>Please can you answer the questions below honestly and accurately:</p>
                <ol>
                  {form.questions.map((question) => (
                    <li key={question}>
                      {personaliseQuestion(question, applicantName)}
                    </li>
                  ))}
                  {additionalQuestions.map((question, index) => (
                    <li key={`additional-question-${index}`}>{question}</li>
                  ))}
                </ol>
              </>
            )}
            {messageAdditionLines.map((line, index) => (
              <p key={`message-addition-${index}`}>{line}</p>
            ))}
            <p>
              If you have any queries do not hesitate to contact me, I look
              forward to hearing from you soon.
            </p>
            </>
          ) : form.referenceKind === "accountant" ? (
            <>
              <p>{getUkGreeting()},</p>
              {followUpIntroduction}
              <p>I hope this email finds you well.</p>
              <p>
                I have been given your details from {applicantName || "the applicant"} in
                regards to a reference
                {form.accountantFrom
                  ? ` covering dates from ${formatDate(form.accountantFrom)}`
                  : " covering dates"}.
              </p>
              {(form.questions?.length > 0 || additionalQuestions.length > 0) && (
                <>
                  <p>
                    Therefore, please may I request that the below questions are
                    answered honestly in regards to {applicantName || "the applicant"}:
                  </p>
                  <ol>
                    {form.questions.map((question) => (
                      <li key={question}>
                        {personaliseQuestion(question, applicantName).replace(
                          /\(Dates\)/gi,
                          form.accountantFrom
                            ? formatDate(form.accountantFrom)
                            : "(Dates)",
                        )}
                      </li>
                    ))}
                    {additionalQuestions.map((question, index) => (
                      <li key={`additional-question-${index}`}>{question}</li>
                    ))}
                  </ol>
                </>
              )}
              {messageAdditionLines.map((line, index) => (
                <p key={`message-addition-${index}`}>{line}</p>
              ))}
              <p>
                If you have any questions, please do not hesitate to contact me.
                I look forward to hearing from you soon.
              </p>
            </>
          ) : isEducationalReference ? (
            <>
              <p>{getUkGreeting()},</p>
              {followUpIntroduction}
              <p>I hope this email finds you well.</p>
              <p>
                I am writing to request a reference for {applicantName || "the applicant"},
                a former student of {form.institutionName || history?.institutionName || history?.nameOfFirm || "your educational institution"}.
                I would be grateful if you could kindly assist by providing
                responses to the following questions:
              </p>
              {(form.questions?.length > 0 || additionalQuestions.length > 0) && (
                <ol>
                  {form.questions.map((question) => (
                    <li key={question}>
                      {personaliseQuestion(
                        question,
                        applicantName,
                        form.institutionName ||
                          history?.institutionName ||
                          history?.nameOfFirm,
                      )}
                    </li>
                  ))}
                  {additionalQuestions.map((question, index) => (
                    <li key={`additional-question-${index}`}>{question}</li>
                  ))}
                </ol>
              )}
              {messageAdditionLines.map((line, index) => (
                <p key={`message-addition-${index}`}>{line}</p>
              ))}
              <p>
                Thank you for your time and assistance. I look forward to
                hearing back from you soon.
              </p>
            </>
          ) : (
            <>
              <p>{getUkGreeting()},</p>
              {followUpIntroduction}
              <p>I hope this email finds you well.</p>
              <p>
                Please may I request for the attached reference for the above
                individual to be completed and returned.
              </p>
              <p>Please see below the details for the individual:</p>
              <ul>
                <li>Name: {applicantName || "Applicant"}</li>
                {nationalInsuranceNumber && (
                  <li>National Insurance Number: {nationalInsuranceNumber}</li>
                )}
                {["trade", "accountant", "educational"].includes(form.referenceKind) ? (
                  <>
                    {form.companyName && <li>Company/Firm: {form.companyName}</li>}
                    {(form.tradingFrom || form.tradingTo) && (
                      <li>
                        Dates of Trading Activity: {[form.tradingFrom, form.tradingTo]
                          .filter(Boolean)
                          .join(" to ")}
                      </li>
                    )}
                    {form.referenceKind === "accountant" && form.accountantFrom && (
                      <li>Accountant Since: {formatDate(form.accountantFrom)}</li>
                    )}
                    {form.referenceKind === "educational" && (
                      <>
                        <li>
                          Education Dates: {formatDate(form.educationFrom)} to{" "}
                          {formatDate(form.educationTo)}
                        </li>
                        {form.educationDetails && (
                          <li>Education Details: {form.educationDetails}</li>
                        )}
                      </>
                    )}
                  </>
                ) : (
                <>
                  <li>
                    Employment Date: {formatDate(form.from)} to{" "}
                    {isCurrentlyEmployed(form.currentlyEmployedAtFirm)
                      ? "Present"
                      : formatDate(form.to)}
                    {role && (
                      <ul>
                        <li>
                          He/she stated that they were {form.employmentType || "employed/self-employed"} as a {role}.
                        </li>
                      </ul>
                    )}
                  </li>
                </>
                )}
              </ul>
            {["trade", "accountant", "educational"].includes(form.referenceKind) &&
              (form.questions?.length > 0 || additionalQuestions.length > 0) && (
              <>
                <p>Please also answer the following questions:</p>
                <ul>
                  {form.questions.map((question) => (
                    <li key={question}>
                      {personaliseQuestion(question, applicantName)}
                    </li>
                  ))}
                  {additionalQuestions.map((question, index) => (
                    <li key={`additional-question-${index}`}>{question}</li>
                  ))}
                </ul>
              </>
            )}
            {messageAdditionLines.map((line, index) => (
              <p key={`message-addition-${index}`}>{line}</p>
            ))}
            <p>
              Please let me know if you have any questions. I look forward to
              hearing from you.
            </p>
            <p>
              Thanks and Regards,
              <br />
              Rosemount Financial Solutions (IFA) Ltd
            </p>
            </>
          )}
          </div>
        </div>

        <footer className="reference-email-modal-footer fit-column gap-s">
          {needsSave && (
            <>
              <label className={`reference-save-confirmation display-flex align-center gap-s FP_Content ${saveError && !form.saveConfirmed ? "reference-save-confirmation--error" : ""}`}>
                <input
                  type="checkbox"
                  checked={Boolean(form.saveConfirmed)}
                  onChange={(event) => {
                    setSaveError("");
                    onSaveConfirmationChange?.(event.target.checked);
                  }}
                />
                <span>I confirm that all reference details are correct before saving.</span>
              </label>
              <div className="display-flex gap-s">
                <SecondaryButton
                  type="button"
                  label={isSaving ? "Saving..." : "Save"}
                  disabled={isSaving}
                  onClick={handleSave}
                />
                <SecondaryButton
                  type="button"
                  label="Cancel"
                  disabled={isSaving}
                  onClick={onClose}
                />
              </div>
            </>
          )}
          {!needsSave && hasAdviserPermission && statusAllowsEmail && (
            <PrimaryButton
              type="button"
              label={
                isSending
                  ? "Sending..."
                  : hasReferenceRequestedDate
                    ? "Send Again"
                    : "Send Email"
              }
              disabled={
                isSending ||
                (requiresAttachments && !hasConsentAttachment) ||
                !canSend
              }
              onClick={handleSend}
            />
          )}
        </footer>
      </section>
    </div>
  );
};

export default ReferenceEmailPreviewModal;
